import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import utc from 'dayjs/plugin/utc'
import { Calendar, ExternalLink, User, CloudOff } from 'lucide-react'

import {
	Card,
	CardContent,
	CardHeader,
} from '@/modules/shared/components/ui/card'
import { cn } from '@/modules/shared/lib/utils'
import { Promotion } from '../types/promotion.type'
import { BsCloudCheck } from 'react-icons/bs'

dayjs.extend(utc)
dayjs.locale('pt-br')

type PromotionCardComponentProps = {
	entity: Promotion
	promotionSelected?: Promotion | null
	selectedPromotions?: Promotion[]
	isMultiSelectMode?: boolean
	onSelectEntity: (entity: Promotion | null) => void
	onLongPress?: (promotion: Promotion) => void
	handleStartView: (visitId?: string) => void
}

export function PromotionCardComponent({
	promotionSelected,
	selectedPromotions = [],
	isMultiSelectMode = false,
	entity,
	onSelectEntity,
	onLongPress,
	handleStartView,
}: PromotionCardComponentProps) {
	const isSelected = isMultiSelectMode
		? selectedPromotions.some(p => p.idVisita === entity.idVisita)
		: promotionSelected?.idVisita === entity.idVisita

	// Long press handling
	let pressTimer: NodeJS.Timeout | null = null

	// Format dates using UTC to prevent timezone issues
	const visitDate = dayjs(entity.data).utc().format('DD/MM/YYYY')
	const inclusionDate = dayjs(entity.dataInclusaoServidor).format('DD/MM/YYYY HH:mm')

	const isSynchronized = !inclusionDate.includes('1899')

	const canBeSelected = !isSynchronized || !isMultiSelectMode

	const handleCardClick = () => {
		if (isSynchronized && isMultiSelectMode) {
			return
		}

		if (isMultiSelectMode) {
			onSelectEntity(entity)
		} else {
			isSelected ? onSelectEntity(null) : onSelectEntity(entity)
		}
	}

	const handleViewClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!isMultiSelectMode) {
			handleStartView(entity.idVisita.toString())
		}
	}

	const handleKeyUp = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !isMultiSelectMode) {
			handleStartView(entity.idVisita.toString())
		}
	}

	const handleLongPress = () => {
		if (!isSynchronized && !isMultiSelectMode && onLongPress) {
			onLongPress(entity)
		}
	}

	const handleMouseDown = () => {
		if (!isMultiSelectMode && !isSynchronized) {
			pressTimer = setTimeout(() => {
				handleLongPress()
			}, 500)
		}
	}

	const handleMouseUp = () => {
		if (pressTimer) {
			clearTimeout(pressTimer)
			pressTimer = null
		}
	}

	const handleMouseLeave = () => {
		if (pressTimer) {
			clearTimeout(pressTimer)
			pressTimer = null
		}
	}

	const formatValue = (value: number) => {
		if (value == null) return 'Valor não informado'
		return new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency: 'BRL'
		}).format(value)
	}

	return (
		<Card
			className={cn(
				'relative transition-all duration-200',
				canBeSelected ? 'cursor-pointer' : 'cursor-default opacity-75',
				isSelected && 'bg-sky-200 border-sky-300',
				!isSelected && 'hover:shadow-md'
			)}
			onClick={handleCardClick}
			onMouseDown={canBeSelected ? handleMouseDown : undefined}
			onMouseUp={canBeSelected ? handleMouseUp : undefined}
			onMouseLeave={canBeSelected ? handleMouseLeave : undefined}
			onTouchStart={canBeSelected ? handleMouseDown : undefined}
			onTouchEnd={canBeSelected ? handleMouseUp : undefined}
		>
			<CardHeader className="p-3 -mb-5 mr-8">
				<div className="flex justify-between items-center text-sm">
					<p>Visita Nº: {entity.idVisita}</p>

					<>
						{isSynchronized ? (
							<BsCloudCheck size={18} className="text-green-600" />
						) : (
							<CloudOff size={18} className="text-gray-500" />
						)}
					</>
				</div>
			</CardHeader>

			<CardContent className="p-3 pb-1 mr-8 z-30">
				<div className="text-sm flex flex-col flex-1 justify-between">
					<div>
						<div className="flex items-center gap-2">
							<User size={14} className="text-gray-500 shrink-0" />
							<p className="font-semibold truncate">{entity.promotor || 'Promotor não informado'}</p>
						</div>
					</div>

					<div className="flex flex-1 justify-between mt-2">
						<div className="flex items-center gap-2">
							<Calendar size={14} className="text-gray-500 shrink-0" />
							<p className="font-medium">{visitDate}</p>
						</div>
					</div>

					<div className="flex flex-1 justify-between mt-2">
						<div className="flex items-center gap-2">
							<p className="font-bold">{formatValue(entity.valor)}</p>
						</div>
					</div>
				</div>
			</CardContent>

			{!isMultiSelectMode && (
				<div
					onKeyUp={handleKeyUp}
					onClick={handleViewClick}
					className={cn(
						'absolute top-0 right-0 p-2 md:p-2 rounded-r-md h-full',
						isSelected ? 'bg-sky-200' : 'bg-gray-200/30',
						'hover:bg-gray-300/50 transition-colors'
					)}
				>
					<ExternalLink className="text-gray-600 ml-1" size={16} />
				</div>
			)}

			{/* Overlay com mensagem para itens sincronizados no modo de seleção múltipla */}
			{isSynchronized && isMultiSelectMode && (
				<div className="absolute inset-0 bg-gray-100/90 flex items-center justify-center">
					<p className="text-xs text-gray-600 text-center p-2">
						Já transmitido
					</p>
				</div>
			)}

			<div className="absolute inset-0 bg-gray-200 opacity-0 pointer-events-none transition-opacity active:opacity-10" />
		</Card>
	)
}